/**
 * Verifica se um código de cor hexadecimal é branco puro ou está em um intervalo próximo ao branco.
 *
 * @param {string} hexColor - O código de cor hexadecimal (ex: '#FFFFFF', '#f0f0f0', 'F9F9F9').
 * @param {number} [threshold=240] - O valor mínimo decimal (0-255) para cada componente RGB
 * considerar a cor "próxima ao branco". Padrão é 240.
 * Um valor maior significa mais próximo do branco puro.
 * @returns {boolean} - Retorna true se a cor for branca ou próxima ao branco, false caso contrário.
 */
export default function isWhiteOrNearWhite(hexColor, threshold = 150) {
    // 1. Valida se a entrada é uma string
    if (typeof hexColor !== 'string') {
        console.error("A entrada deve ser uma string hexadecimal de cor.");
        return false;
    }

    // 2. Remove o '#' se existir
    let cleanHex = hexColor.startsWith('#') ? hexColor.slice(1) : hexColor;

    // 3. Converte shorthand de 3 dígitos para 6 dígitos (ex: #FFF -> #FFFFFF)
    if (cleanHex.length === 3) {
        cleanHex = cleanHex[0] + cleanHex[0] + cleanHex[1] + cleanHex[1] + cleanHex[2] + cleanHex[2];
    }

    // 4. Valida o comprimento (deve ser 6 dígitos agora)
    if (cleanHex.length !== 6) {
        console.error("Código hexadecimal de cor inválido.");
        return false;
    }

    // 5. Extrai os componentes R, G, B e converte de hexadecimal para decimal
    const rHex = cleanHex.substring(0, 2);
    const gHex = cleanHex.substring(2, 4);
    const bHex = cleanHex.substring(4, 6);

    const rDec = parseInt(rHex, 16);
    const gDec = parseInt(gHex, 16);
    const bDec = parseInt(bHex, 16);

    // 6. Valida se a conversão foi bem sucedida e os valores estão no intervalo correto (0-255)
    if (isNaN(rDec) || isNaN(gDec) || isNaN(bDec) ||
        rDec < 0 || rDec > 255 ||
        gDec < 0 || gDec > 255 ||
        bDec < 0 || bDec > 255) {
        console.error("Código hexadecimal de cor inválido ou fora do intervalo RGB.");
        return false;
    }

    // 7. Valida o limite (threshold)
    if (typeof threshold !== 'number' || threshold < 0 || threshold > 255) {
        console.warn(`Limite (threshold) inválido (${threshold}). Usando o padrão 240.`);
        threshold = 240;
    }

    // 8. Verifica se todos os componentes (R, G, B) são maiores ou iguais ao limite
    // Para ser considerado "próximo ao branco" (e quase invisível em branco),
    // todos os componentes RGB devem ter uma intensidade alta.
    return rDec >= threshold && gDec >= threshold && bDec >= threshold;
}